import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import Loading from "../Common/Loading";
import ConfirmDialog from "../Common/ConfirmDialog";
import SideModal from "./SideModal";
import AddBulkicon from "../assets/AddBulkicon.png";
import { API } from "../Common/Api";
import { ErrorToast, SuccessToast } from "../Utils/toast";

// A dealer/agent can only be deleted once nothing is assigned to or activated
// under them (and a dealer must have no agents left). The backend enforces the
// same rule — this just keeps the icon hidden until it can succeed.
const hasNoInventory = (user) => !(user.assignedCount > 0) && !(user.activatedCount > 0);

// Admin: dealers under a distributor. Each dealer row expands to reveal that
// dealer's own agents (the distributor -> dealer -> agent chain).
const DistributorDealersModal = ({ distributor, onClose, setModalOpen }) => {
  const [loading, setLoading] = useState(false);
  const [dealers, setDealers] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name, role }
  const [deleting, setDeleting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let active = true;
    const fetchDealers = async () => {
      setLoading(true);
      try {
        const res = await API.GetDealersDistributorWise({ distributorId: distributor?._id });
        if (!active) return;
        // Filtered by distributorId, so the first row is our distributor.
        const list = res?.data?.data?.[0]?.dealers || [];
        setDealers(Array.isArray(list) ? list : []);
      } catch (e) {
        if (active) {
          setDealers([]);
          ErrorToast("Failed to fetch dealers for this distributor");
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchDealers();
    return () => { active = false; };
  }, [distributor?._id, refreshKey]);

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await API.DeleteDealerOrAgent(deleteTarget.id);
      if (res?.status === 200 && res?.success) {
        SuccessToast(res?.message || "Deleted successfully");
        setDeleteTarget(null);
        setRefreshKey((k) => k + 1); // re-fetch the list
      } else {
        ErrorToast(res?.message || "Failed to delete");
        setDeleteTarget(null);
      }
    } catch (e) {
      ErrorToast("Failed to delete");
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const DeleteButton = ({ id, name, role }) => (
    <button
      onClick={() => setDeleteTarget({ id, name, role })}
      title={`Delete ${role}`}
      aria-label={`Delete ${role} ${name}`}
      className="inline-flex items-center justify-center p-1.5 rounded-md text-gray-400 hover:text-[#EE3B31] hover:bg-rose-50 transition-colors"
    >
      <Trash2 className="w-4 h-4" />
    </button>
  );

  return (
    <SideModal
      title={`Dealers for ${distributor?.username}`}
      image={AddBulkicon}
      onClose={onClose}
      setModalOpen={setModalOpen}
      width="800px"
    >
      <div className="mt-4 overflow-x-auto relative min-h-[300px]">
        <Loading loading={loading} overlay="table" />
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Dealer Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Mobile Number</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Agents</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Assigned</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Activated</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Show Agent</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Delete</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {dealers.length > 0 ? (
              dealers.map((dealer) => {
                const agents = dealer.agents || [];
                const isOpen = !!expanded[dealer._id];
                return (
                  <React.Fragment key={dealer._id}>
                    <tr>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {dealer.username}
                        {dealer.email && <div className="text-xs text-gray-500">{dealer.email}</div>}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{dealer.mobileNo}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-gray-500">{dealer.agentCount ?? agents.length ?? 0}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-gray-500">{dealer.assignedCount || 0}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-gray-500">{dealer.activatedCount || 0}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-center">
                        <button
                          onClick={() => toggle(dealer._id)}
                          className="inline-flex items-center gap-1 text-blue-600 font-medium hover:underline"
                        >
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          View Agents
                        </button>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-center">
                        {hasNoInventory(dealer) && agents.length === 0 && (
                          <DeleteButton id={dealer._id} name={dealer.username} role="dealer" />
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan="7" className="px-4 py-3 bg-gray-50">
                          {agents.length > 0 ? (
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead>
                                <tr>
                                  <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase whitespace-nowrap">Agent Name</th>
                                  <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase whitespace-nowrap">Mobile Number</th>
                                  <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase whitespace-nowrap">Email</th>
                                  <th className="px-3 py-2 text-center text-[11px] font-semibold text-gray-500 uppercase whitespace-nowrap">Assigned</th>
                                  <th className="px-3 py-2 text-center text-[11px] font-semibold text-gray-500 uppercase whitespace-nowrap">Activated</th>
                                  <th className="px-3 py-2 text-center text-[11px] font-semibold text-gray-500 uppercase whitespace-nowrap">Delete</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {agents.map((agent) => (
                                  <tr key={agent._id}>
                                    <td className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">{agent.username}</td>
                                    <td className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap">{agent.mobileNo}</td>
                                    <td className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap">{agent.email || "-"}</td>
                                    <td className="px-3 py-2 text-sm text-center text-gray-500 whitespace-nowrap">{agent.assignedCount || 0}</td>
                                    <td className="px-3 py-2 text-sm text-center text-gray-500 whitespace-nowrap">{agent.activatedCount || 0}</td>
                                    <td className="px-3 py-2 text-sm text-center whitespace-nowrap">
                                      {hasNoInventory(agent) && (
                                        <DeleteButton id={agent._id} name={agent.username} role="agent" />
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="text-sm text-gray-500 py-2">No agents found for this dealer</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              !loading && (
                <tr>
                  <td colSpan="7" className="px-4 py-4 text-center text-sm text-gray-500">
                    No dealers found for this distributor
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>

      {/* Portaled to <body>: SideModal's slide-in transform would otherwise make
          this fixed overlay relative to the panel instead of the screen. */}
      {createPortal(
        <ConfirmDialog
          open={!!deleteTarget}
          title={`Delete ${deleteTarget?.role || "user"} permanently?`}
          message={`"${deleteTarget?.name}" will be permanently deleted. This cannot be undone — after deletion this user can't be activated or sign in again.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          loading={deleting}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />,
        document.body
      )}
    </SideModal>
  );
};

export default DistributorDealersModal;
